import React, { useState, useEffect } from 'react';
import { createEmptyToolData, createEmptyLap } from '../types/magical';
import MagicalCounter from './MagicalCounter';
import MagicalStats from './MagicalStats';
import MagicalHistory from './MagicalHistory';

const STORAGE_KEY = 'magical-tool-data';

const Magical = () => {
  const [toolData, setToolData] = useState(createEmptyToolData());
  const [currentLap, setCurrentLap] = useState(createEmptyLap());
  const [currentSection, setCurrentSection] = useState('counter');
  const [isSubtractionMode, setIsSubtractionMode] = useState(false);

  useEffect(() => {
    document.title = '魔法少女モード判別ツール - あんこの中の夢';
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setToolData(parsed.toolData || createEmptyToolData());
        setCurrentLap(parsed.currentLap || createEmptyLap());
      } catch (e) {
        console.error('Failed to parse saved data:', e);
      }
    }
  }, []);

  const saveToStorage = (newToolData, newCurrentLap) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      toolData: newToolData,
      currentLap: newCurrentLap
    }));
  };

  const handleSuikaCount = () => {
    if (isSubtractionMode && toolData.total.suikaCount > 0 && currentLap.suikaCount > 0) {
      const newToolData = {
        ...toolData,
        total: {
          ...toolData.total,
          suikaCount: toolData.total.suikaCount - 1
        }
      };
      const newCurrentLap = {
        ...currentLap,
        suikaCount: currentLap.suikaCount - 1
      };
      setToolData(newToolData);
      setCurrentLap(newCurrentLap);
      saveToStorage(newToolData, newCurrentLap);
    } else if (!isSubtractionMode) {
      const newToolData = {
        ...toolData,
        total: {
          ...toolData.total,
          suikaCount: toolData.total.suikaCount + 1
        }
      };
      const newCurrentLap = {
        ...currentLap,
        suikaCount: currentLap.suikaCount + 1
      };
      setToolData(newToolData);
      setCurrentLap(newCurrentLap);
      saveToStorage(newToolData, newCurrentLap);
    }
  };

  const handleCzCount = () => {
    if (isSubtractionMode && toolData.total.czCount > 0 && currentLap.czCount > 0) {
      const newToolData = {
        ...toolData,
        total: {
          ...toolData.total,
          czCount: toolData.total.czCount - 1
        }
      };
      const newCurrentLap = {
        ...currentLap,
        czCount: currentLap.czCount - 1
      };
      setToolData(newToolData);
      setCurrentLap(newCurrentLap);
      saveToStorage(newToolData, newCurrentLap);
    } else if (!isSubtractionMode) {
      const newToolData = {
        ...toolData,
        total: {
          ...toolData.total,
          czCount: toolData.total.czCount + 1
        }
      };
      const newCurrentLap = {
        ...currentLap,
        czCount: currentLap.czCount + 1
      };
      setToolData(newToolData);
      setCurrentLap(newCurrentLap);
      saveToStorage(newToolData, newCurrentLap);
    }
  };

  const handleEyeCatch = (charId, strength) => {
    if (isSubtractionMode && currentLap.eyeCatches[charId][strength] > 0) {
      const newCurrentLap = {
        ...currentLap,
        eyeCatches: {
          ...currentLap.eyeCatches,
          [charId]: {
            ...currentLap.eyeCatches[charId],
            [strength]: currentLap.eyeCatches[charId][strength] - 1
          }
        }
      };
      setCurrentLap(newCurrentLap);
      saveToStorage(toolData, newCurrentLap);
    } else if (!isSubtractionMode) {
      const newCurrentLap = {
        ...currentLap,
        eyeCatches: {
          ...currentLap.eyeCatches,
          [charId]: {
            ...currentLap.eyeCatches[charId],
            [strength]: currentLap.eyeCatches[charId][strength] + 1
          }
        }
      };
      setCurrentLap(newCurrentLap);
      saveToStorage(toolData, newCurrentLap);
    }
  };

  const addLap = (bonusType = 'bb') => {
    const lapWithBonusType = {
      ...currentLap,
      bonusType
    };
    const newToolData = {
      ...toolData,
      laps: [...toolData.laps, lapWithBonusType]
    };
    const newCurrentLap = createEmptyLap();
    setToolData(newToolData);
    setCurrentLap(newCurrentLap);
    saveToStorage(newToolData, newCurrentLap);
  };

  const resetAll = () => {
    if (window.confirm('全データをリセットしますか？この操作は取り消せません。')) {
      const newToolData = createEmptyToolData();
      const newCurrentLap = createEmptyLap();
      setToolData(newToolData);
      setCurrentLap(newCurrentLap);
      saveToStorage(newToolData, newCurrentLap);
    }
  };

  const undoLastLap = () => {
    if (toolData.laps.length === 0) return;
    
    if (window.confirm('最後のラップを取り消して、データを現在のラップに戻しますか？')) {
      const lastLap = toolData.laps[toolData.laps.length - 1];
      const newToolData = {
        ...toolData,
        laps: toolData.laps.slice(0, -1)
      };
      const newEyeCatches = {};
      Object.keys(currentLap.eyeCatches).forEach(charId => {
        newEyeCatches[charId] = {
          strong: currentLap.eyeCatches[charId].strong + lastLap.eyeCatches[charId].strong,
          weak: currentLap.eyeCatches[charId].weak + lastLap.eyeCatches[charId].weak
        };
      });
      
      const newCurrentLap = {
        ...currentLap,
        suikaCount: currentLap.suikaCount + lastLap.suikaCount,
        czCount: currentLap.czCount + lastLap.czCount,
        eyeCatches: newEyeCatches,
        bonusType: lastLap.bonusType || 'bb'
      };
      setToolData(newToolData);
      setCurrentLap(newCurrentLap);
      saveToStorage(newToolData, newCurrentLap);
    }
  };

  const updateLapExcludeFlag = (lapId, excludeFromStats) => {
    const newToolData = {
      ...toolData,
      laps: toolData.laps.map(lap => 
        lap.lapId === lapId ? { ...lap, excludeFromStats } : lap
      )
    };
    setToolData(newToolData);
    saveToStorage(newToolData, currentLap);
  };

  const deleteLastLap = () => {
    if (toolData.laps.length === 0) return;
    
    if (window.confirm('最後のラップを削除しますか？データは戻されません。')) {
      const lastLap = toolData.laps[toolData.laps.length - 1];
      const newToolData = {
        ...toolData,
        total: {
          suikaCount: toolData.total.suikaCount - lastLap.suikaCount,
          czCount: toolData.total.czCount - lastLap.czCount
        },
        laps: toolData.laps.slice(0, -1)
      };
      setToolData(newToolData);
      saveToStorage(newToolData, currentLap);
    }
  };

  const sections = [
    { id: 'counter', label: 'カウンター', icon: '📊' },
    { id: 'stats', label: '統計', icon: '📈' },
    { id: 'history', label: '履歴', icon: '📋' }
  ];

  const renderSection = () => {
    switch (currentSection) {
      case 'counter':
        return (
          <>
            {/* ボーナス当選ボタン */}
            <div className="flex justify-center gap-3 mb-4">
              <button 
                onClick={() => addLap('bb')}
                className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg shadow-sm"
              >
                ボーナス当選
              </button>
              <button 
                onClick={() => addLap('at')}
                className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg shadow-sm"
              >
                AT当選
              </button>
            </div>
            {toolData.laps.length > 0 && (
              <div className="text-xs text-gray-500 text-center mb-4">
                最後のラップ: {new Date(toolData.laps[toolData.laps.length - 1].createdAt).toLocaleString('ja-JP')}
              </div>
            )}
            <MagicalCounter
              currentLap={currentLap}
              handleSuikaCount={handleSuikaCount}
              handleCzCount={handleCzCount}
              handleEyeCatch={handleEyeCatch}
              isSubtractionMode={isSubtractionMode}
              setIsSubtractionMode={setIsSubtractionMode}
            />
          </>
        );
      case 'stats':
        return <MagicalStats toolData={toolData} currentLap={currentLap} />;
      case 'history':
        return <MagicalHistory toolData={toolData} undoLastLap={undoLastLap} deleteLastLap={deleteLastLap} resetAll={resetAll} updateLapExcludeFlag={updateLapExcludeFlag} />;
      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <h1 className="text-lg font-bold text-center mb-0">魔法少女モード判別ツール</h1>
      
      {/* ナビゲーション */}
      <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
        <div className="flex flex-wrap justify-center gap-2">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setCurrentSection(section.id)}
              className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                currentSection === section.id
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {section.icon} {section.label}
            </button>
          ))}
        </div>
      </div>


      {/* セクション表示 */}
      {renderSection()}
    </div>
  );
};

export default Magical;